import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import type { Statement } from './historyApi'
import { kindLabel, packageLine, signedMoney } from './historyLabels'
import { formatDay } from './historyPeriod'

const cell: CSSProperties = {
  border: '0.3mm solid #999',
  padding: '1.2mm 1.6mm',
  textAlign: 'start',
  verticalAlign: 'top',
}
const numeric: CSSProperties = { ...cell, textAlign: 'end', whiteSpace: 'nowrap' }
const head: CSSProperties = { ...cell, background: '#eee', fontWeight: 600 }
const summary: CSSProperties = { ...cell, fontWeight: 600, background: '#f6f6f6' }

export function StatementDocument({ statement }: { statement: Statement }) {
  const { t } = useTranslation()
  const { company, customer, period } = statement
  const columns = 6

  const details: [string, string][] = [
    [t('history.statement.customer'), customer.name ?? customer.login],
    [t('history.statement.login'), customer.login],
    [t('history.statement.customerId'), customer.customerId ?? t('common.dash')],
    [
      t('history.statement.period'),
      t('history.statement.periodValue', { from: formatDay(period.from), to: formatDay(period.to) }),
    ],
    [t('history.statement.generatedAt'), formatDateTime(statement.generatedAt)],
  ]

  return (
    <div style={{ color: '#000', background: '#fff', fontSize: '9.5pt', lineHeight: 1.35 }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6mm',
          borderBottom: '0.5mm solid #000',
          paddingBottom: '3mm',
          marginBottom: '4mm',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
          {company.logoUrl ? (
            <img src={company.logoUrl} alt="" style={{ maxHeight: '14mm', maxWidth: '40mm' }} />
          ) : null}
          {company.name ? <strong style={{ fontSize: '12pt' }}>{company.name}</strong> : null}
        </div>
        <h1 style={{ margin: 0, fontSize: '15pt' }}>{t('history.statement.title')}</h1>
      </header>

      <table style={{ borderCollapse: 'collapse', marginBottom: '4mm' }}>
        <tbody>
          {details.map(([label, value]) => (
            <tr key={label}>
              <th style={{ textAlign: 'start', fontWeight: 400, color: '#555', paddingInlineEnd: '4mm' }}>
                {label}
              </th>
              <td style={{ fontWeight: 600 }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>{t('history.col.at')}</th>
            <th style={head}>{t('history.col.operation')}</th>
            <th style={head}>{t('history.col.package')}</th>
            <th style={{ ...head, textAlign: 'end' }}>{t('history.col.amount')}</th>
            <th style={{ ...head, textAlign: 'end' }}>{t('history.col.balanceAfter')}</th>
            <th style={head}>{t('history.col.document')}</th>
          </tr>
        </thead>
        <tbody>
          <tr data-row="opening">
            <td style={summary} colSpan={4}>
              {t('history.openingBalance')}
            </td>
            <td style={{ ...summary, textAlign: 'end', whiteSpace: 'nowrap' }}>
              {formatMoneyValue(statement.opening)}
            </td>
            <td style={summary} colSpan={columns - 5} />
          </tr>
          {statement.rows.map((row) => (
            <tr key={row.txnId} data-row="txn" style={{ breakInside: 'avoid' }}>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatDateTime(row.at)}</td>
              <td style={cell}>{kindLabel(row.kind, t)}</td>
              <td style={cell}>{packageLine(row) ?? ''}</td>
              <td style={numeric}>{signedMoney(row.amount)}</td>
              <td style={numeric}>{formatMoneyValue(row.balanceAfter)}</td>
              <td style={cell}>{row.reference ?? ''}</td>
            </tr>
          ))}
          <tr data-row="closing">
            <td style={summary} colSpan={4}>
              {t('history.closingBalance')}
            </td>
            <td style={{ ...summary, textAlign: 'end', whiteSpace: 'nowrap' }}>
              {formatMoneyValue(statement.closing)}
            </td>
            <td style={summary} colSpan={columns - 5} />
          </tr>
        </tbody>
      </table>

      {statement.rows.length === 0 ? (
        <p style={{ marginTop: '3mm' }}>{t('history.empty')}</p>
      ) : null}
      {statement.truncated ? (
        <p style={{ marginTop: '3mm', fontWeight: 600 }}>
          {t('history.statement.truncated', { count: statement.rows.length })}
        </p>
      ) : null}
    </div>
  )
}
